/**
 * Persistent client-side activity log for the embedded Shopify dashboard.
 *
 * We keep two pieces of state in localStorage so that merchants who refresh
 * the page (or come back to the embedded admin later) can still see whether
 * their last "Sync now" / "Disconnect" click actually went anywhere.
 *
 * Storage keys (always namespaced by shop domain so multi-store merchants
 * never see cross-shop state):
 *
 *   erpclaw.shopify.last-sync-request.<shop>
 *     -> JSON: { requestedAt: ISO8601, deliveryTier: "active"|"scheduled"|"on-demand" }
 *     One entry. Overwritten on each new "Sync now" click.
 *     Cleared once status.last_sync_at advances past requestedAt.
 *
 *   erpclaw.shopify.activity.<shop>
 *     -> JSON: ActivityEntry[]   (capped at MAX_ACTIVITY entries, newest first)
 *     One entry per "Sync now" or "Disconnect" click.
 */

export type ActivityType = "sync-now" | "disconnect";
export type DeliveryTier = "active" | "scheduled" | "on-demand";

export type ActivityEntry = {
  type: ActivityType;
  requestedAt: string;
  dispatchedAt?: string;
  dispatchTier?: DeliveryTier;
  note?: string;
};

export type LastSyncRequest = {
  requestedAt: string;
  deliveryTier: DeliveryTier;
};

export const MAX_ACTIVITY = 5;

const ACTIVITY_PREFIX = "erpclaw.shopify.activity.";
const LAST_SYNC_PREFIX = "erpclaw.shopify.last-sync-request.";

function activityKey(shop: string): string {
  return `${ACTIVITY_PREFIX}${shop}`;
}

function lastSyncKey(shop: string): string {
  return `${LAST_SYNC_PREFIX}${shop}`;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Read the activity log for a given shop. Returns [] on missing or corrupt data. */
export function readActivity(shop: string): ActivityEntry[] {
  const ls = safeStorage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(activityKey(shop));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is ActivityEntry => {
      if (!e || typeof e !== "object") return false;
      const obj = e as Record<string, unknown>;
      return (
        (obj.type === "sync-now" || obj.type === "disconnect") &&
        typeof obj.requestedAt === "string"
      );
    });
  } catch {
    return [];
  }
}

/**
 * Append an activity entry for a shop. Trims oldest entries beyond MAX_ACTIVITY.
 * Newest entry is first in the returned array.
 */
export function appendActivity(shop: string, entry: ActivityEntry): ActivityEntry[] {
  const ls = safeStorage();
  const existing = readActivity(shop);
  const next = [entry, ...existing].slice(0, MAX_ACTIVITY);
  if (ls) {
    try {
      ls.setItem(activityKey(shop), JSON.stringify(next));
    } catch {
      // quota or disabled; non-fatal
    }
  }
  return next;
}

/**
 * Mark any activity entries whose requestedAt is older than dispatchedAtIso
 * as dispatched. Used when the status blob's last_sync_at advances and we
 * want to back-fill the UI.
 */
export function markDispatchedBefore(
  shop: string,
  dispatchedAtIso: string,
  type: ActivityType,
): ActivityEntry[] {
  const ls = safeStorage();
  const existing = readActivity(shop);
  const dispatchedMs = new Date(dispatchedAtIso).getTime();
  let changed = false;
  const next = existing.map((e) => {
    if (e.type !== type) return e;
    if (e.dispatchedAt) return e;
    const reqMs = new Date(e.requestedAt).getTime();
    if (reqMs <= dispatchedMs) {
      changed = true;
      return { ...e, dispatchedAt: dispatchedAtIso };
    }
    return e;
  });
  if (changed && ls) {
    try {
      ls.setItem(activityKey(shop), JSON.stringify(next));
    } catch {
      // non-fatal
    }
  }
  return next;
}

/** Read the most recent sync-now request for a shop, or null. */
export function readLastSyncRequest(shop: string): LastSyncRequest | null {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(lastSyncKey(shop));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.requestedAt !== "string") return null;
    if (
      obj.deliveryTier !== "active" &&
      obj.deliveryTier !== "scheduled" &&
      obj.deliveryTier !== "on-demand"
    ) {
      return null;
    }
    return { requestedAt: obj.requestedAt, deliveryTier: obj.deliveryTier };
  } catch {
    return null;
  }
}

/** Persist the most recent sync-now request for a shop. */
export function writeLastSyncRequest(shop: string, value: LastSyncRequest): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.setItem(lastSyncKey(shop), JSON.stringify(value));
  } catch {
    // non-fatal
  }
}

/** Clear the persisted sync-now request for a shop. */
export function clearLastSyncRequest(shop: string): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.removeItem(lastSyncKey(shop));
  } catch {
    // non-fatal
  }
}

/** Format an ISO8601 timestamp as HH:MM in the merchant's local timezone. */
export function formatTimeHHMM(iso: string): string {
  const d = new Date(iso);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
